<template>
  <div>
    <div class="find-user-container" :class="{ 'no-autocomplete': groupTypeProp !== 0 }">
      <InputGroup class="add-input-group">
        <InputGroupAddon class="hidden md:flex" v-if="showAddComponente">
          <i :class="findIcon"></i>
        </InputGroupAddon>
        <InputGroupAddon v-else>
          <i class="pi pi-search"></i>
        </InputGroupAddon>
        <InputGroupAddon
          v-if="type == 'group' && groupTypes.length"
          class="select-group-type"
        >
          <Dropdown
            class="select-group-type-dropdown"
            v-model="groupType"
            :options="groupTypes"
            optionLabel="name"
            optionValue="value"
            :disabled="fromUserHasGroups || groupTypes.length == 1"
            @change="handleOnChangeGroupType"
          />
        </InputGroupAddon>
        <AutoComplete
          v-if="showAddComponente"
          ref="autoCompleteRef"
          v-model="selectedUser"
          :suggestions="findUsers"
          :placeholder="autocompletePlaceholder"
          class="w-full autocomplete"
          appendTo="self"
          @complete="search"
          @clear="handleClear"
          @item-select="addUser"
          :delay="600"
        >
          <template #option="slotProps">
            <template v-if="type == 'user'">
              <div>
                <b>{{ slotProps.option.nombre }}</b> ({{ slotProps.option.email }})
              </div>
              <div>{{ slotProps.option.unidad }}</div>
            </template>
            <template v-else-if="type == 'group'">
              <template v-if="groupType === 0">
                {{ slotProps.option.nombre }}
              </template>
              <template v-else-if="groupType === 1">
                {{ slotProps.option.displayName }}
              </template>
              <template v-else-if="groupType === 2">
                {{ slotProps.option.nombre }}
              </template>
            </template>
          </template>
        </AutoComplete>
      </InputGroup>

      <InputGroup class="search-input-group">
        <InputGroupAddon v-if="groupTypeProp === 0">
          <i class="pi pi-search"></i>
        </InputGroupAddon>
        <InputText 
          v-model="searchFilter" 
          :placeholder="type == 'user' ? 'Filtrar usuarios...' : 'Filtrar grupos...'"
          @input="handleSearchFilter"
        />
      </InputGroup>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import AutoComplete from "primevue/autocomplete";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";

import { appUserListAll, resetTotalTries } from "@/api/users/users.api.js";
import {
  appUserGroupListAll,
  appUserGroupListAD,
  appUserGroupListUpruc,
} from "@/api/users-groups/users-groups.api.js";
import { useAuthorization } from "@sttedra_ui/framework";

// #region Props, Emits
const emit = defineEmits(["add-user", "search-users"]);
const props = defineProps({
  type: {
    type: String,
    default: "user",
  },
  idInforme: {
    type: String,
    default: "",
  },
  fromUserHasGroups: {
    type: Boolean,
    default: false,
  },
  fromUserGroupHasUsers: {
    type: Boolean,
    default: false,
  },
  groupTypeProp: {
    type: Number,
    default: 0,
  },
  canManageUsers:{
    type: Boolean,
    default: false,
  }
});
// #endregion

// #region Params, Ref, Reactives, Computed
const { modelRef: authorizationModelRef } = useAuthorization();
const userRol = authorizationModelRef.value.roles[0];
const getTypeIcon = computed(() => {
  if (
    userRol != "Admin" ||
    groupTypes.value.length == 0 ||
    props.groupTypeProp == 1 ||
    props.groupTypeProp == 2
  ) {
    return "pi pi-search";
  }

  return "pi pi-plus";
});

const findIcon = computed(() => {
  if (loading.value) {
    return "pi pi-spin pi-spinner";
  }

  return getTypeIcon.value;
});
const autoCompleteRef = ref(null);
const loading = ref(false);
const selectedUser = ref(null);
const findUsers = ref([]);
const groupType = ref(0);
const searchFilter = ref("");
const groupTypes = ref([
  { name: "Locales", value: 0 },
  { name: "AD", value: 1 },
  { name: "UPRUC", value: 2 },
]);

const showAddComponente = computed(() => {
  return (userRol === "Admin" && !(groupTypes.value.length == 0 ||
    groupTypes.value.length == 0 ||
    props.groupTypeProp == 1 ||
    props.groupTypeProp == 2) || props.canManageUsers
  );
});

const autocompletePlaceholder = computed(() => {
  if (
    groupTypes.value.length == 0 ||
    props.groupTypeProp == 1 ||
    props.groupTypeProp == 2
  ) {
    return "Filtrar grupo de usuarios";
  } else {
    if (props.type == "user") {
      return "Añadir usuario";
    } else if (props.type == "group") {
      return "Añadir grupo";
    }
  }
});
const selectedImpersonation = ref(null);
// #endregion

// #region Functions
function addUser(event) {
  emit(
    "add-user",
    props.type == "user"
      ? event.value
      : {
        group: event.value,
        groupType: groupType.value,
      },
  );
  selectedUser.value = null;
}

function handleClear() {
  search({ query: "" });
}

function handleSearchFilter() {
  emit("search-users", searchFilter.value);
}

async function search(event) {
  const query = event.query;
  resetTotalTries();

  let params = {};
  let response = null;

  loading.value = true;

  if (props.fromUserGroupHasUsers && props.groupTypeProp !== 0) {
    loading.value = false;
    return;
  }

  switch (props.type) {
    case "user":
      params = {
        email: query,
      };

      if (props.idInforme) {
        params.reportId = props.idInforme;
      }

      findUsers.value = await appUserListAll(params);
      loading.value = false;
      break;

    case "group":
      params = null;

      switch (groupType.value) {
        case 0:
          params = {
            filter: query,
            TipoGrupo: 0, // Locales
          };

          findUsers.value = await appUserGroupListAll(params);
          break;

        case 1:
          params = {
            name: query,
          };

          response = await appUserGroupListAD(params);

          if (response.status == 200) {
            findUsers.value = response.data.activeDirectoryGroups;
          } else {
            findUsers.value = [];
          }

          break;

        case 2:
          params = {
            name: query,
          };

          response = await appUserGroupListUpruc(params);

          if (response.status == 200) {
            findUsers.value = response.data.uprucGroups;
          } else {
            findUsers.value = [];
          }

          break;
      }

      loading.value = false;
      break;
  }
}

function handleOnChangeGroupType() {
  if (!selectedUser.value) return;

  if (autoCompleteRef.value) {
    autoCompleteRef.value.onInput({ target: { value: selectedUser.value } });
  }
}

// #endregion

// #region Effects, Hooks
onMounted(() => {
  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    selectedImpersonation.value = JSON.parse(impersonationToken);
  }

  if (
    userRol != "Admin" ||
    (selectedImpersonation.value != null &&
      selectedImpersonation.value?.esAdministrador === false)
  ) {
    const useLocalUserGroups = STTEDRA_USER_GROUPS_LOCAL;
    const useADUserGroups = STTEDRA_USER_GROUPS_AD;
    const useUPRUCUserGroups = STTEDRA_USER_GROUPS_UPRUC;

    // Remove item from groupTypes if not using local user groups
    if (!useLocalUserGroups || useLocalUserGroups == "false") {
      groupTypes.value = groupTypes.value.filter((item) => item.value !== 0);
    }

    // Remove item from groupTypes if not using AD user groups
    if (!useADUserGroups || useADUserGroups == "false") {
      groupTypes.value = groupTypes.value.filter((item) => item.value !== 1);
    }

    // Remove item from groupTypes if not using UPRUC user groups
    if (!useUPRUCUserGroups || useUPRUCUserGroups == "false") {
      groupTypes.value = groupTypes.value.filter((item) => item.value !== 2);
    }

    if (groupTypes.value.length > 0) {
      groupType.value = groupTypes.value[0].value;
    } else {
      groupType.value = null;
    }
  }
});
// #endregion

// #region Provide, Expose
defineExpose({
  loading,
});
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.find-user-container {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  
  &.no-autocomplete {
    .add-input-group {
      flex: 0 0 auto;
      width: auto;
    }
    
    .search-input-group {
      flex: 1;
    }
  }
}

.add-input-group {
  flex: 2;
  min-width: 0;
}

.autocomplete {
  :deep(.p-autocomplete-input) {
    width: 100%;
  }
}

.select-group-type {
  width: 12rem;
  padding: 0;

  .select-group-type-dropdown {
    border: 0;
    border-radius: 0;

    &.p-focus {
      box-shadow: none;
    }
  }
}

.search-input-group {
  flex: 1;
  min-width: 0;
  flex-shrink: 0;
}

@media (min-width: $mobile-min-width) and (max-width: $mobile-max-width) {
  .autocomplete {
    max-width: unset;

    :deep(.p-autocomplete-input) {
      border-radius: 0;
    }
  }

  .select-group-type {
    min-width: 8rem;
  }
  
  .search-input-group {
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .find-user-container {
    flex-direction: column;
  }
  
  .add-input-group {
    flex-wrap: wrap;
  }
  
  .autocomplete {
    flex-basis: 100%;
    width: 100%;
  }
  
  .select-group-type {
    flex-basis: 100%;
    width: 100%;
    min-width: unset;
  }
  
  .search-input-group {
    max-width: 100%;
  }
}

</style>